import { addToolBreadcrumb } from '@tunasong/models'
import {
  computeAugDimWarmupSessionStats,
  computeIntervalSessionStats,
  computeQualitySessionStats,
  generateAugDimPair,
  generateIntervalQuestion,
  generateQualityQuestion,
  validateAugDimAnswer,
  validateIntervalAnswer,
  validateQualityAnswer,
} from '@tunasong/music-lib'
import type {
  AugDimWarmupPair,
  AugDimWarmupSessionResult,
  AugDimWarmupVerdict,
  ChordQualityKind,
  IntervalDirection,
  IntervalKind,
  IntervalQuestion,
  IntervalSessionResult,
  IntervalVerdict,
  QualityQuestion,
  QualitySessionResult,
  QualityVerdict,
} from '@tunasong/music-lib'
import { useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { AugDimWarmupFeedback } from './aug-dim-warmup-feedback.js'
import { useAugDimWarmupPlayback } from './aug-dim-warmup-playback.js'
import { AugDimWarmupQuestion } from './aug-dim-warmup-question.js'
import { EarTrainingSummary } from './ear-training-summary.js'
import { EarTrainingWelcome } from './ear-training-welcome.js'
import { IntervalFeedback } from './interval-feedback.js'
import { IntervalQuestionPanel } from './interval-question.js'
import { useIntervalPlayback } from './interval-playback.js'
import { parseDirection, parseSubMode, type DirectionParam, type SubMode } from './ear-training-params.js'
import { QualityFeedback } from './quality-feedback.js'
import { QualityQuestionPanel } from './quality-question.js'
import { useQualityPlayback } from './quality-playback.js'
import styles from './ear-training.module.css'

const QUESTIONS_PER_SESSION = 10
const AUTOPLAY_DELAY_MS = 250

type SessionState =
  | { phase: 'welcome'; preSelected: SubMode | null; preSelectedDirection: DirectionParam | null }
  | {
      phase: 'asking-interval'
      index: number
      question: IntervalQuestion
      results: IntervalSessionResult[]
      direction: DirectionParam
    }
  | {
      phase: 'feedback-interval'
      index: number
      question: IntervalQuestion
      verdict: IntervalVerdict
      results: IntervalSessionResult[]
      direction: DirectionParam
    }
  | { phase: 'done-interval'; results: IntervalSessionResult[] }
  | { phase: 'asking-quality'; index: number; question: QualityQuestion; results: QualitySessionResult[] }
  | {
      phase: 'feedback-quality'
      index: number
      question: QualityQuestion
      verdict: QualityVerdict
      results: QualitySessionResult[]
    }
  | { phase: 'done-quality'; results: QualitySessionResult[] }
  | { phase: 'asking-warmup'; index: number; pair: AugDimWarmupPair; results: AugDimWarmupSessionResult[] }
  | {
      phase: 'feedback-warmup'
      index: number
      pair: AugDimWarmupPair
      verdict: AugDimWarmupVerdict
      results: AugDimWarmupSessionResult[]
    }
  | { phase: 'done-warmup'; results: AugDimWarmupSessionResult[] }

/**
 * Choose one of {ascending, descending, harmonic} per question when the user
 * selected Mixed. 1/3 weight each (Bowie §Q2).
 */
const drawMixedDirection = (): IntervalDirection => {
  const r = Math.random()
  if (r < 1 / 3) {
    return 'ascending'
  }
  if (r < 2 / 3) {
    return 'descending'
  }
  return 'harmonic'
}

/**
 * Resolve the per-question direction passed to the analyzer:
 *  - mixed → randomly draws one of the 3 base directions per question
 *  - otherwise → the picked direction itself
 */
const resolveDirection = (picked: DirectionParam): IntervalDirection =>
  picked === 'mixed' ? drawMixedDirection() : picked

/**
 * Ear Training Suite v1.5 host. v1's two sub-modes + the new Aug/Dim Warm-up.
 * Interval ID gains a direction picker on the welcome screen and a per-
 * question direction (Mixed re-draws per question).
 *
 * Spec: docs/superpowers/specs/2026-05-21-ear-training-v15-design.md
 */
export const EarTraining: FC = () => {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<SessionState>(() => ({
    phase: 'welcome',
    preSelected: parseSubMode(searchParams.get('mode')),
    preSelectedDirection: parseDirection(searchParams.get('direction')),
  }))

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'ear-training' })
  }, [])

  // Active questions / pair for playback hooks. A null payload yields an empty
  // stab list (no-op play), so hooks mount unconditionally.
  const intervalQuestion: IntervalQuestion | null =
    state.phase === 'asking-interval' || state.phase === 'feedback-interval' ? state.question : null
  const qualityQuestion: QualityQuestion | null =
    state.phase === 'asking-quality' || state.phase === 'feedback-quality' ? state.question : null
  const warmupPair: AugDimWarmupPair | null =
    state.phase === 'asking-warmup' || state.phase === 'feedback-warmup' ? state.pair : null
  const { play: playInterval } = useIntervalPlayback({ question: intervalQuestion })
  const { play: playQuality } = useQualityPlayback({ question: qualityQuestion })
  const { play: playWarmup } = useAugDimWarmupPlayback({ pair: warmupPair })

  // Autoplay on each new asking-* mount.
  const askingIntervalQuestion = state.phase === 'asking-interval' ? state.question : null
  useEffect(() => {
    if (askingIntervalQuestion === null) {
      return
    }
    const handle = window.setTimeout(playInterval, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingIntervalQuestion, playInterval])
  const askingQualityQuestion = state.phase === 'asking-quality' ? state.question : null
  useEffect(() => {
    if (askingQualityQuestion === null) {
      return
    }
    const handle = window.setTimeout(playQuality, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingQualityQuestion, playQuality])
  const askingWarmupPair = state.phase === 'asking-warmup' ? state.pair : null
  useEffect(() => {
    if (askingWarmupPair === null) {
      return
    }
    const handle = window.setTimeout(playWarmup, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingWarmupPair, playWarmup])

  const startInterval = (direction: DirectionParam) => {
    const question = generateIntervalQuestion(undefined, { direction: resolveDirection(direction) })
    setState({ phase: 'asking-interval', index: 0, question, results: [], direction })
  }
  const startQuality = () => {
    setState({ phase: 'asking-quality', index: 0, question: generateQualityQuestion(), results: [] })
  }
  const startWarmup = () => {
    setState({ phase: 'asking-warmup', index: 0, pair: generateAugDimPair(), results: [] })
  }
  const start = (subMode: SubMode, direction?: DirectionParam) => {
    if (subMode === 'interval') {
      startInterval(direction ?? 'ascending')
      return
    }
    if (subMode === 'quality') {
      startQuality()
      return
    }
    startWarmup()
  }

  const answerInterval = (picked: IntervalKind) => {
    if (state.phase !== 'asking-interval') {
      return
    }
    const verdict = validateIntervalAnswer(state.question, picked)
    addToolBreadcrumb('answered', { tool: 'ear-training', subMode: 'interval', correct: verdict.correct })
    setState({
      phase: 'feedback-interval',
      index: state.index,
      question: state.question,
      verdict,
      results: state.results,
      direction: state.direction,
    })
  }
  const answerQuality = (picked: ChordQualityKind) => {
    if (state.phase !== 'asking-quality') {
      return
    }
    const verdict = validateQualityAnswer(state.question, picked)
    addToolBreadcrumb('answered', { tool: 'ear-training', subMode: 'quality', correct: verdict.correct })
    setState({
      phase: 'feedback-quality',
      index: state.index,
      question: state.question,
      verdict,
      results: state.results,
    })
  }
  const answerWarmup = (picked: 'first' | 'second') => {
    if (state.phase !== 'asking-warmup') {
      return
    }
    const verdict = validateAugDimAnswer(state.pair, picked)
    addToolBreadcrumb('answered', { tool: 'ear-training', subMode: 'aug-dim-warmup', correct: verdict.correct })
    setState({
      phase: 'feedback-warmup',
      index: state.index,
      pair: state.pair,
      verdict,
      results: state.results,
    })
  }

  const nextInterval = () => {
    if (state.phase !== 'feedback-interval') {
      return
    }
    const results: IntervalSessionResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-interval', results })
      return
    }
    const question = generateIntervalQuestion(undefined, { direction: resolveDirection(state.direction) })
    setState({
      phase: 'asking-interval',
      index: state.index + 1,
      question,
      results,
      direction: state.direction,
    })
  }
  const nextQuality = () => {
    if (state.phase !== 'feedback-quality') {
      return
    }
    const results: QualitySessionResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-quality', results })
      return
    }
    setState({
      phase: 'asking-quality',
      index: state.index + 1,
      question: generateQualityQuestion(),
      results,
    })
  }
  const nextWarmup = () => {
    if (state.phase !== 'feedback-warmup') {
      return
    }
    const results: AugDimWarmupSessionResult[] = [
      ...state.results,
      { pair: state.pair, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-warmup', results })
      return
    }
    setState({
      phase: 'asking-warmup',
      index: state.index + 1,
      pair: generateAugDimPair(),
      results,
    })
  }

  /**
   * Round-robin sub-mode cycle: interval → quality → aug-dim-warmup → interval.
   * Surfaces the next sub-mode pre-selected on the welcome screen.
   */
  const switchMode = (lastSubMode: SubMode) => {
    const nextMode: SubMode =
      lastSubMode === 'interval' ? 'quality' : lastSubMode === 'quality' ? 'aug-dim-warmup' : 'interval'
    setState({ phase: 'welcome', preSelected: nextMode, preSelectedDirection: null })
  }

  const footerTools: RelatedTool[] = [
    { targetRoute: '/app/quiz', lexiconId: 'crosslink-ear-to-quiz' },
    { targetRoute: '/app/modes', lexiconId: 'crosslink-ear-to-mode' },
    { targetRoute: '/app/reharm', lexiconId: 'crosslink-ear-to-reharm' },
    { targetRoute: '/app/cadences', lexiconId: 'crosslink-ear-to-cadence' },
  ]

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <EarTrainingWelcome
          preSelected={state.preSelected}
          preSelectedDirection={state.preSelectedDirection}
          onStart={start}
        />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'asking-interval') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <IntervalQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={playInterval}
          onAnswer={answerInterval}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-interval') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <IntervalQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={playInterval}
          onAnswer={() => {
            /* feedback phase — disabled via interactive={false} */
          }}
        />
        <IntervalFeedback
          question={state.question}
          verdict={state.verdict}
          onHearAgain={playInterval}
          onNext={nextInterval}
        />
      </div>
    )
  }

  if (state.phase === 'asking-quality') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <QualityQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={playQuality}
          onAnswer={answerQuality}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-quality') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <QualityQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={playQuality}
          onAnswer={() => {
            /* feedback phase — disabled via interactive={false} */
          }}
        />
        <QualityFeedback
          question={state.question}
          verdict={state.verdict}
          onHearAgain={playQuality}
          onNext={nextQuality}
        />
      </div>
    )
  }

  if (state.phase === 'asking-warmup') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <AugDimWarmupQuestion
          pair={state.pair}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={playWarmup}
          onAnswer={answerWarmup}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-warmup') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <AugDimWarmupQuestion
          pair={state.pair}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={playWarmup}
          onAnswer={() => {
            /* feedback phase — disabled via interactive={false} */
          }}
        />
        <AugDimWarmupFeedback
          pair={state.pair}
          verdict={state.verdict}
          onHearAgain={playWarmup}
          onNext={nextWarmup}
        />
      </div>
    )
  }

  if (state.phase === 'done-interval') {
    return (
      <div className={styles.container}>
        <EarTrainingSummary
          subMode="interval"
          stats={computeIntervalSessionStats(state.results)}
          onRestart={() => startInterval('ascending')}
          onSwitchMode={switchMode}
        />
      </div>
    )
  }

  if (state.phase === 'done-quality') {
    return (
      <div className={styles.container}>
        <EarTrainingSummary
          subMode="quality"
          stats={computeQualitySessionStats(state.results)}
          onRestart={startQuality}
          onSwitchMode={switchMode}
        />
      </div>
    )
  }

  // state.phase === 'done-warmup'
  return (
    <div className={styles.container}>
      <EarTrainingSummary
        subMode="aug-dim-warmup"
        stats={computeAugDimWarmupSessionStats(state.results)}
        onRestart={startWarmup}
        onSwitchMode={switchMode}
      />
    </div>
  )
}
